import { ProgressCircle, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useState } from 'react';

// Ordered to mirror the real research pipeline (graphs/competitor_analyze):
// resolve identity → read the site → socials + follower counts → reviews →
// recent posts → assemble the profile. The backend stamps the competitor doc
// only once, at the end of the run, so there's no finer signal to read; these
// labels narrate the known sequence rather than guess. They advance once and
// CLAMP on the final label — they never loop, so a long run doesn't visibly
// restart and read as fake.
const STATUS_LABELS_NAME = [
	'Finding their website…',
	'Reading their website…',
	'Checking their social profiles…',
	'Counting their followers…',
	'Looking up their reviews…',
	'Reading their recent posts…',
	'Putting it all together…',
];
// When the user seeded a URL the website is already known, so skip the
// find-the-site step and start by reading it.
const STATUS_LABELS_URL = STATUS_LABELS_NAME.slice(1);
const STATUS_ROTATE_MS = 9000;

function prefersReducedMotion(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	);
}

// Placeholder card shown in place of a CompetitorCard while a newly-seeded
// business is being researched. Mirrors the real card's geometry (61px avatar,
// title line, body rows) so the swap to the populated card doesn't jump.
export function ResearchingCard({
	title,
	seedKind = 'name',
}: {
	title: string;
	seedKind?: 'name' | 'url';
}) {
	const labels = seedKind === 'url' ? STATUS_LABELS_URL : STATUS_LABELS_NAME;
	const [statusIndex, setStatusIndex] = useState(0);

	useEffect(() => {
		if (prefersReducedMotion()) return;
		const interval = setInterval(() => {
			// Advance once through the sequence, then stop on the final label —
			// no modulo wrap, so the narration never visibly restarts.
			setStatusIndex((prev) => {
				if (prev >= labels.length - 1) {
					clearInterval(interval);
					return prev;
				}
				return prev + 1;
			});
		}, STATUS_ROTATE_MS);
		return () => clearInterval(interval);
	}, [labels.length]);

	return (
		<div
			className="glass-card-sm p-6"
			role="status"
			aria-live="polite"
			aria-busy="true"
			tabIndex={0}
		>
			<div className="flex items-start gap-4">
				<div className="research-shimmer h-[61px] w-[61px] shrink-0 rounded-xl" />
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<Text
						styles={style({
							font: 'body',
							fontSize: 'body-lg',
							fontWeight: 'bold',
							color: 'title',
						})}
					>
						{title}
					</Text>
					<div className="flex items-center gap-2">
						<ProgressCircle size="S" isIndeterminate aria-label="Researching" />
						{/* The first label is announced to assistive tech; later
							rotations are hidden so screen readers aren't spammed. */}
						<Text
							aria-hidden={statusIndex > 0}
							styles={style({
								font: 'body',
								fontSize: 'body-sm',
								color: 'neutral',
							})}
						>
							{labels[statusIndex]}
						</Text>
					</div>
					<div className="mt-2 flex flex-col gap-2">
						<div className="research-shimmer h-3 w-3/4 rounded-md" />
						<div className="research-shimmer h-3 w-1/2 rounded-md" />
						<div className="research-shimmer h-3 w-2/3 rounded-md" />
					</div>
				</div>
			</div>
		</div>
	);
}
